import { atom, atomFamily } from 'recoil';
import * as H from 'history';
import moment from 'moment-timezone';

import * as K from 'common/constants';
import { exists } from 'common/functions';
import UrlParamsByRoute from 'common/params';
import { UrlParams } from 'common/types';
import { ResultsTab, View } from 'views/types';
import {
  addQueryTermsToFilter,
  buildParamsWithCompleteFilter,
  buildParamsWithDeconstructedFilter,
  buildPowerQueryWithCompleteFilter,
  simplifyFilter,
} from 'common/util/filter/filter';
import {
  buildUrl,
  getScalyrPath,
  getUrlParam,
  getUrlParamSet,
  getWhitelistedUrlParams,
  removeParamsFromUrl,
  updateUrlParams,
} from 'common/util/location';
import { generateQueryTimeRange, QueryTimeRange } from 'common/util/time';
import { recoilSelector } from 'common/util/recoil-selector';
import { availableAllTeamsEmailAddressesState } from 'components/account/account.state';
import { dashboardState } from 'components/dashboards/dashboards.state';
import { Dashboard } from 'components/dashboards/types';
import { settingsState } from 'components/common/Managers/SettingsManager.state';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { currentResultsTabState, viewFieldsState, viewState } from 'components/common/Managers/ViewStateManager.state';

export const DEFAULT_LOCATION = {
  hash: '',
  pathname: '',
  search: '',
};

export const CROSS_TEAM_SEARCH_ROUTES = [K.ROUTE.EVENTS, K.ROUTE.POWER_QUERY, K.ROUTE.GRAPH, K.ROUTE.DISTRIBUTION];

/**
 * Route that was served when the SPA was initiated.
 */
export const ROUTE_FROM_SERVER = getScalyrPath(window?.location || DEFAULT_LOCATION);

/**
 * Token representing the currently selected log line. Defaults to URL value of the continuationToken parameter if any.
 */
export const selectedTokenState = atom<string>({
  key: K.GLOBAL_KEY.SELECTED_TOKEN_STATE,
  default: getUrlParam(window?.location?.search || '', K.URL_PARAM.CONTINUATION_TOKEN),
});

/**
 * Value of the teamToken URL parameter.
 */
export const teamTokenState = atom({
  key: K.RECOIL_ATOM_KEY.TEAM_TOKEN,
  default: getUrlParam(window?.location?.search || '', K.URL_PARAM.TEAM_TOKEN),
});

/**
 * Immutable state representation of the location from history
 */
export const locationState = atom<H.Location>({
  key: K.RECOIL_ATOM_KEY.LOCATION,
  default: (() => {
    const { hash, pathname, search } = window.location || DEFAULT_LOCATION;
    // `key` is a unique string associated with this location. Always `default` on initial location
    return { hash, pathname, search, state: undefined, key: 'default' };
  })(),
});

/**
 * All event query string parameters from the current location.search {@see UrlParamsByRoute}
 *
 * Note that, at least for now, this selector cannot be cached efficiently because {@link getWhitelistedUrlParams}
 * always returns a new reference.
 *
 * Also note that {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-5615} could remove most use cases for this
 * selector. Furthermore, using this selector only in recoil callbacks, such as those called for by FRONT-5615,
 * would negate any performance penalty.
 */
export const eventParamsState = recoilSelector<UrlParams>({
  key: K.RECOIL_SELECTOR_KEY.EVENT_PARAMS,
  get: ({ get }) => {
    const location = get(locationState);
    const { eventParamList } = getUrlParamSet(location);
    return getWhitelistedUrlParams(location.search, eventParamList);
  },
});

/**
 * All event and view query string parameters from the current location.search {@see UrlParamsByRoute}
 *
 * Note that, at least for now, this selector cannot be cached efficiently because {@link getWhitelistedUrlParams}
 * always returns a new reference.
 *
 */
export const searchParamRecordState = recoilSelector<UrlParams>({
  key: K.RECOIL_SELECTOR_KEY.SEARCH_PARAM_RECORD,
  get: ({ get }) => {
    const location = get(locationState);
    const { eventParamList, viewParamList } = getUrlParamSet(location);
    return getWhitelistedUrlParams(location.search, [...eventParamList, ...viewParamList]);
  },
});

/**
 * Returns the value of a query string parameter from the current location.search
 */
export const searchParamState = atomFamily<string, K.URL_PARAM>({
  key: K.RECOIL_ATOM_KEY.SEARCH_PARAM,
  default: (paramName) => getUrlParam(window?.location?.search || '', paramName),
});

/**
 * This collection tracks user inputs that might become {@link searchParamState} but haven't yet been committed in a
 * search. Draft params are tracked separately for each route.
 */
export const draftSearchParamByRouteState = atomFamily<UrlParams, K.ROUTE>({
  key: K.RECOIL_ATOM_KEY.DRAFT_SEARCH_PARAMS_BY_ROUTE,
  default: {} as UrlParams,
});

/**
 * Returns the most recently stored eventParam. We store during updateUrlState call.
 */
export const eventParamRecordState = atom<UrlParams | {}>({
  key: K.RECOIL_ATOM_KEY.EVENT_PARAM_RECORD,
  default: {},
});

/**
 * Route from the current location
 */
export const routeState = atom<K.ROUTE>({
  key: K.RECOIL_ATOM_KEY.ROUTE,
  default: getScalyrPath(window?.location || DEFAULT_LOCATION),
});

/**
 * This collection tracks user inputs that might become {@link searchParamState} but haven't yet been committed in a
 * search. The atomic state itself is isolated by route in {@see draftSearchParamByRouteState}, so this writable
 * selector is provided as a convenience to read and write to draft params for the current route.
 */
export const draftSearchParamState = recoilSelector<UrlParams>({
  key: K.RECOIL_SELECTOR_KEY.DRAFT_SEARCH_PARAMS,
  get: ({ get }) => {
    const route = get(routeState);
    return get(draftSearchParamByRouteState(route));
  },
  set: ({ get, set }, newValue) => {
    const route = get(routeState);
    set(draftSearchParamByRouteState(route), newValue);
  },
});

/**
 * {@link Dashboard} the user is currently viewing, if any.
 */
export const currentDashboardState = recoilSelector<Dashboard | null>({
  key: K.RECOIL_SELECTOR_KEY.CURRENT_DASHBOARD,
  get: ({ get }) => {
    const route = get(routeState);
    const page = get(searchParamState(K.URL_PARAM.DASHBOARD_NAME));
    if (route === K.ROUTE.DASHBOARD) {
      return get(dashboardState(page));
    }
    return null;
  },
});

/**
 * Returns the query string param value for the CrossTeamSearch teamEmails
 */
export const teamEmailsStringFromParamsState = recoilSelector<string | undefined>({
  key: K.RECOIL_SELECTOR_KEY.TEAM_EMAILS_STRING_FROM_PARAMS,
  get: ({ get }) => get(searchParamState(K.URL_PARAM.TEAM_EMAILS)),
});

/**
 * Returns list of emails for the teamEmails URL param, if not present, returns undefined.
 *
 * There are three possible states:
 * 1. There is a `teamEmails` param defined with a comma-separated list of emails (one or more).
 *    In this case, this function returns an array of emails
 * 2. There is a `teamEmails` param defined with the NO_TEAM_EMAILS_PARAM value, so no team has been explicitly been selected.
 *    In this case, this function returns empty array.
 * 3. There is no `teamEmails` param defined, so the user hasn't made any explicit selection.
 *    In this case, this function returns undefined.
 */
export const teamEmailsFromParamsState = recoilSelector<Array<string> | undefined>({
  key: K.RECOIL_SELECTOR_KEY.TEAM_EMAILS_FROM_PARAMS,
  get: ({ get }) => {
    const teamEmailsParam = get(teamEmailsStringFromParamsState);

    if (teamEmailsParam === K.NO_TEAM_EMAILS_PARAM) {
      return [];
    }

    // We need to make sure it's a non empty string, otherwise we'll return ['']
    // And also need to avoid having an empty string as an element of the resulting array, otherwise, the API will error
    return teamEmailsParam && teamEmailsParam.length > 0 ? teamEmailsParam.split(',').filter(exists) : undefined;
  },
});

/**
 * True if current view is a cross team search
 */
export const isCrossTeamSearchState = recoilSelector<boolean>({
  key: K.RECOIL_ATOM_KEY.IS_CROSS_TEAM_SEARCH,
  get: ({ get }) => {
    const route = get(routeState);
    const teamEmails = get(teamEmailsFromParamsState);
    const currentResultsTab = get(currentResultsTabState);
    // Results tab display settings includes view display settings (and could potentially override it)
    const tabDisplaySettings = currentResultsTab?.displaySettings;

    return (
      CROSS_TEAM_SEARCH_ROUTES.includes(route) &&
      (Array.isArray(teamEmails) || Boolean(tabDisplaySettings?.searchAllTeamsByDefault))
    );
  },
});

/**
 * Returns list of emails for the Cross Team Search if the current search is a cross team search, otherwise undefined.
 * This list comes from either teamEmails URL param if set, or from all available teams if the current view has
 * `searchAllTeamsByDefault` set.
 */
export const teamEmailsState = recoilSelector<Array<string> | undefined>({
  key: K.RECOIL_ATOM_KEY.TEAM_EMAILS,
  get: ({ get }) => {
    const isCrossTeamSearch = get(isCrossTeamSearchState);
    const teamEmailsFromParams = get(teamEmailsFromParamsState);
    const currentResultsTab = get(currentResultsTabState);
    const tabDisplaySettings = currentResultsTab?.displaySettings;

    if (isCrossTeamSearch) {
      // If there is a query param, we honor it
      if (teamEmailsFromParams) {
        return teamEmailsFromParams;
      }

      // Otherwise, we should check for the view and tab display settings
      if (tabDisplaySettings?.searchAllTeamsByDefault) {
        const availableAllTeamsEmailAddresses = get(availableAllTeamsEmailAddressesState);

        return availableAllTeamsEmailAddresses;
      }
    }
  },
});

/**
 * The currently active (i.e. most recently issued) request time for LRQ
 */
export const activeRequestTimeState = atom<moment.Moment | undefined>({
  key: K.RECOIL_ATOM_KEY.ACTIVE_REQUEST_TIME,
  default: undefined,
});

export const DEFAULT_QUERY_TIME_RANGE = { startTime: '', endTime: '', startTimeNs: '', endTimeNs: '', requestTime: '' };

/**
 * Default start time for searches in the current context, which might include a custom {@link defaultSearchTimeSpan}
 * in /scaly/logs or a duration configuration property on a dashboard.
 */
export const defaultStartTime = recoilSelector<string>({
  key: K.GLOBAL_KEY.DEFAULT_START_TIME,
  get: ({ get }) => {
    const fromSettings = get(settingsState)[K.SETTING.DEFAULT_START_TIME];
    const fromDashboardConfig = get(currentDashboardState)?.config?.duration;

    return fromDashboardConfig || fromSettings;
  },
});

/**
 * Object representing a resolved time range for LRQ
 */
export const queryTimeRangeState = recoilSelector<QueryTimeRange>({
  key: K.RECOIL_SELECTOR_KEY.QUERY_TIME_RANGE,
  get: ({ get }) => {
    const activeRequestTime = get(activeRequestTimeState);
    if (!activeRequestTime) {
      return DEFAULT_QUERY_TIME_RANGE;
    }

    let activeRequestTimeClone = activeRequestTime.clone();
    const route = get(routeState);
    if (route === K.ROUTE.DASHBOARD) {
      activeRequestTimeClone.subtract(30, 'seconds');
    }

    const startTime = get(searchParamState(K.URL_PARAM.START_TIME));
    const endTime = get(searchParamState(K.URL_PARAM.END_TIME));
    const defaultStart = get(defaultStartTime);
    const timezone = get(timezoneState);

    // Although generateQueryTimeRange can be impure, we only use the pure mode
    return generateQueryTimeRange(timezone, startTime, endTime, defaultStart, activeRequestTimeClone);
  },
});

/**
 * The current origin from window.location.origin
 */
export const originState = atom<string>({
  key: K.RECOIL_ATOM_KEY.ORIGIN,
  default: window?.location?.origin || '',
});

/**
 * The current absolute app url, but as an absolute time range with the currently displayed resolved query time range
 */
export const absoluteResolvedUrlState = recoilSelector<string>({
  key: K.RECOIL_SELECTOR_KEY.ABSOLUTE_RESOLVED_URL,
  get: ({ get }) => {
    const location = get(locationState);
    const queryTimeRange = get(queryTimeRangeState);
    const origin = get(originState);
    const relativeUrl = buildUrl(
      location.pathname,
      updateUrlParams(location.search, {
        [K.URL_PARAM.START_TIME]: queryTimeRange.startTime,
        [K.URL_PARAM.END_TIME]: queryTimeRange.endTime,
      } as UrlParams),
    );
    return origin + relativeUrl;
  },
});

/**
 * The appropriate {@see PowerQueriesPage} url given the current location. The url should always lead to a new search
 * when navigating from the {@see PowerQueriesPage} itself. Otherwise, it is built with the current event params that
 * match those used by the {@see PowerQueriesPage}. The exception being `graphStyle` event param. We want to ensure
 * when navigating to the PQ page, we start with a clean slate and show the default `table` view.
 */
export const powerQueriesPageUrlState = recoilSelector<string>({
  key: K.RECOIL_SELECTOR_KEY.POWER_QUERIES_PAGE_URL,
  get: ({ get }) => {
    const eventParams = get(eventParamsState);
    const route = get(routeState);
    const powerQueryParameters = [K.ROUTE.POWER_QUERY, K.ROUTE.DASHBOARD].includes(route)
      ? ({} as UrlParams)
      : getWhitelistedUrlParams(
          addQueryTermsToFilter(eventParams),
          UrlParamsByRoute[K.ROUTE.POWER_QUERY].eventParamList.filter((param) => param !== K.URL_PARAM.GRAPH_STYLE),
        );

    return buildUrl(K.ROUTE.POWER_QUERY, powerQueryParameters);
  },
});

/**
 * The appropriate {@see SearchLogsPage} url given the current location. The url should always lead to a new search
 * when navigating from the {@see SearchLogsPage} itself. Otherwise, it is built with the current event params that
 * match those used by the {@see SearchLogsPage}. This selector returns the simplified version of complex filters from
 * graphs or PowerQueries.
 */
export const searchLogsPageUrlState = recoilSelector<string>({
  key: K.RECOIL_SELECTOR_KEY.SEARCH_LOGS_PAGE_URL,
  get: ({ get }) => {
    const eventParams = get(eventParamsState);
    const route = get(routeState);
    const view = get(viewState);
    const searchParameters = [K.ROUTE.EVENTS, K.ROUTE.DASHBOARD].includes(route)
      ? ({} as UrlParams)
      : (getWhitelistedUrlParams(
          buildParamsWithDeconstructedFilter(eventParams, view),
          UrlParamsByRoute[K.ROUTE.EVENTS].eventParamList,
        ) as UrlParams);
    searchParameters[K.URL_PARAM.FILTER] = simplifyFilter(searchParameters[K.URL_PARAM.FILTER]);

    return buildUrl(K.ROUTE.EVENTS, searchParameters);
  },
});

/**
 * A function that returns a URL for the given dashboard based on the current location's time range.
 */
export const getDashboardUrlState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.DASHBOARD_URL,
  get: ({ get }) => {
    const eventParams = get(eventParamsState);
    const dashboardParameters = getWhitelistedUrlParams(addQueryTermsToFilter(eventParams), [
      K.URL_PARAM.START_TIME,
      K.URL_PARAM.END_TIME,
    ]) as UrlParams;

    return (dashboardName: string): string =>
      buildUrl(K.ROUTE.DASHBOARD, { ...dashboardParameters, [K.URL_PARAM.DASHBOARD_NAME]: dashboardName });
  },
});

/**
 * The appropriate {@see LiveTailPage} url given the current location.
 */
export const liveTailPageUrlState = recoilSelector<string>({
  key: K.RECOIL_SELECTOR_KEY.LIVE_TAIL_PAGE_URL,
  get: ({ get }) => {
    const searchParams = get(searchParamRecordState);
    const view = get(viewState);
    const adjustedParams = buildParamsWithCompleteFilter(searchParams, view);
    const liveTailParams = getWhitelistedUrlParams(adjustedParams, UrlParamsByRoute[K.ROUTE.LIVE_TAIL].eventParamList);

    return buildUrl(K.ROUTE.LIVE_TAIL, liveTailParams);
  },
});

/**
 * The appropriate {@see BulkExportsPage} url given the current location.
 */
export const bulkExportsPageUrlState = recoilSelector<string>({
  key: K.RECOIL_SELECTOR_KEY.BULK_EXPORTS_PAGE_URL,
  get: ({ get }) => {
    const searchParams = get(searchParamRecordState);
    const view = get(viewState);
    const resultsTab = get(currentResultsTabState);
    const adjustedParams = buildParamsWithCompleteFilter(searchParams, view, resultsTab);
    const bulkExportsParams = getWhitelistedUrlParams(
      adjustedParams,
      UrlParamsByRoute[K.ROUTE.BULK_EXPORTS].eventParamList,
    );

    return buildUrl(K.ROUTE.BULK_EXPORTS, bulkExportsParams);
  },
});

/**
 * The current relative URL less `teamToken`. Useful for switching to the same URL on another team.
 */
export const teamSwitchUrlState = recoilSelector<string>({
  key: K.RECOIL_SELECTOR_KEY.TEAM_SWITCH_URL,
  get: ({ get }) => {
    const location = get(locationState);
    return removeParamsFromUrl(location, [K.URL_PARAM.TEAM_TOKEN]);
  },
});

/**
 * The appropriate {@see LinkAccountPage} url for the quick switch "...more" link given the current location. The
 * current {@see teamSwitchUrlState} is captured as the `originalUrl` param for the app to redirect to after switching
 * to another team.
 */
export const quickSwitchLinkAccountPageUrlState = recoilSelector<string>({
  key: K.RECOIL_SELECTOR_KEY.QUICK_SWITCH_LINK_ACCOUNT_PAGE_URL,
  get: ({ get }) => {
    const route = get(routeState);
    // This is a corner case, but we should not continue to build nested originalUrl redirects. This is consistent with
    // other similar cases; generic links to the same page that that the user is currently viewing have a clean slate.
    if (route === K.ROUTE.MANAGE_TEAMS) {
      return K.ROUTE.MANAGE_TEAMS;
    }

    const teamSwitchUrl = get(teamSwitchUrlState);
    return buildUrl(K.ROUTE.MANAGE_TEAMS, { [K.URL_PARAM.ORIGINAL_URL]: teamSwitchUrl } as UrlParams);
  },
});

/**
 * On the search and graphs pages, filter state is distributed among different parts of the application state.
 * For example, query string params like "logSource" or a view's internal filter need to be combined with the
 * filter input to construct the complete filter. A complete filter is necessary when transitioning to many pages
 * in the UI or querying the API.
 */
export const completeFilterState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.COMPLETE_FILTER,
  get: ({ get }) => {
    const searchParams = get(searchParamRecordState);
    const view = get(viewState);
    const resultsTab = get(currentResultsTabState);
    const adjustedParams = buildParamsWithCompleteFilter(searchParams, view, resultsTab);
    return adjustedParams[K.URL_PARAM.FILTER] ?? '';
  },
});

/**
 * Like {@ref completeFilterState} but without taking current tab filter into account.
 */
export const completeFilterWithoutTabState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.COMPLETE_FILTER_WITHOUT_TABS,
  get: ({ get }) => {
    const searchParams = get(searchParamRecordState);
    const view = get(viewState);
    const adjustedParams = buildParamsWithCompleteFilter(searchParams, view);
    return adjustedParams[K.URL_PARAM.FILTER] ?? '';
  },
});

/**
 * The filter portion of a PQ (before any commands) should include the filter from the current view if there is one.
 * We also need to add any columns needed for rendering. This selector returns an object with the new PQ and a list
 * of columns that were added.
 */
export const completePowerQueryState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.COMPLETE_POWER_QUERY,
  get: ({ get }) => {
    const searchParams = get(searchParamRecordState);
    const view = get(viewState);
    const viewFields = get(viewFieldsState);
    return buildPowerQueryWithCompleteFilter(searchParams[K.URL_PARAM.FILTER], view, viewFields);
  },
});

/**
 * Return a function that will generate the URL for a given view. That URL keeps the user on the current page. The time
 * range is not resolved, so a relative range will remain relative.
 */
export const getViewUrlState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.VIEW_URL,
  get: ({ get }) => {
    const eventParams = get(eventParamsState);
    const route = get(routeState);
    const currentView = get(viewState);
    const getViewUrl = (view: View) => {
      const searchParameters = getWhitelistedUrlParams(
        buildParamsWithDeconstructedFilter(eventParams, currentView),
        UrlParamsByRoute[K.ROUTE.EVENTS].eventParamList,
      ) as UrlParams;
      searchParameters[K.URL_PARAM.VIEW] = view.name;
      return buildUrl(route, searchParameters);
    };
    return getViewUrl;
  },
});

/**
 * Return a function that will generate the URL for a given results tab. Currently only the /events page shows result tabs.
 * We use the resolved time range because each tab is tied to the results of the current search.
 */
export const getResultsTabUrlState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.RESULTS_TAB_URL,
  get: ({ get }) => {
    const eventParams = get(eventParamsState);
    const route = get(routeState);
    const queryTimeRange = get(queryTimeRangeState);
    const teamEmailsFromParams = get(teamEmailsStringFromParamsState);
    const currentView = get(viewState);
    const getLinkForResultsTab = (tab: ResultsTab) => {
      const searchParameters = getWhitelistedUrlParams(
        buildParamsWithDeconstructedFilter(eventParams, currentView),
        UrlParamsByRoute[K.ROUTE.EVENTS].eventParamList,
      ) as UrlParams;
      searchParameters[K.URL_PARAM.START_TIME] = queryTimeRange.startTime;
      searchParameters[K.URL_PARAM.END_TIME] = queryTimeRange.endTime;
      searchParameters[K.URL_PARAM.RESULTS_TAB] = tab.id;
      if (teamEmailsFromParams) {
        searchParameters[K.URL_PARAM.TEAM_EMAILS] = teamEmailsFromParams;
      }
      return buildUrl(route, searchParameters);
    };

    return getLinkForResultsTab;
  },
});
